import React, { useMemo } from "react";
import { X } from "lucide-react";
import type { InsightChipRef, InsightConnectivityRef, InsightRouteEntry, InsightTopologyResponse } from "../../types/insightApi";
import { insightNodeTypeFor, type InsightNodeData } from "./topologyNodes";
import { normalizeCanonicalTopologyNodes } from "./canonicalDigest";
import { attachmentFamilyOf, type VpcAttachmentFamily } from "./hierarchyDisclosure";

/** Human titles for the VPC-attachment families a synthetic `<vpcId>::attachment-<family>` selection can name. */
const ATTACHMENT_FAMILY_TITLE: Record<VpcAttachmentFamily, string> = {
	igw: "Internet Gateways",
	nat: "NAT Gateways",
	tgw: "Transit Gateway attachments",
	peering: "VPC Peering connections",
	directConnect: "Direct Connect gateways",
};

/** Parses the synthetic id of an attachment pill drawn above a VPC card (`<vpcId>::attachment-<family>`; `all` is the "+N more" overflow pill), `null` for every real resource id. */
function parseAttachmentSelection(selectedResourceId: string): { vpcId: string; family: VpcAttachmentFamily | "all" } | null {
	const match = /^(.+)::attachment-(igw|nat|tgw|peering|directConnect|all)$/.exec(selectedResourceId);
	if (!match) return null;
	return { vpcId: match[1], family: match[2] as VpcAttachmentFamily | "all" };
}

const ATTACHMENT_FAMILY_ORDER: VpcAttachmentFamily[] = ["igw", "nat", "tgw", "peering", "directConnect"];

/** Every gateway of `family` under `vpcId` in the digest — the same classification the canvas nodes use, re-derived from the topology so the sidebar works from real, navigable resources. */
function attachmentGatewaysFor(
	topology: InsightTopologyResponse,
	vpcId: string,
	family: VpcAttachmentFamily
): InsightTopologyResponse["nodes"] {
	const childrenByParent = new Map<string, InsightTopologyResponse["nodes"]>();
	topology.nodes.forEach((candidate) => {
		if (!candidate.parentId) return;
		const list = childrenByParent.get(candidate.parentId) ?? [];
		list.push(candidate);
		childrenByParent.set(candidate.parentId, list);
	});
	const gateways: InsightTopologyResponse["nodes"] = [];
	const stack = [...(childrenByParent.get(vpcId) ?? [])];
	while (stack.length > 0) {
		const candidate = stack.pop() as InsightTopologyResponse["nodes"][number];
		stack.push(...(childrenByParent.get(candidate.id) ?? []));
		const kind = candidate.type ?? String((candidate.data as InsightNodeData | undefined)?.kind ?? "");
		if (insightNodeTypeFor(kind, candidate.kind) !== "insightGateway") continue;
		if (attachmentFamilyOf(kind) === family) gateways.push(candidate);
	}
	return gateways;
}

/**
 * A route table's next-hop summary and its individual routes. Its own component
 * because both lists are capped with `useCappedList`, and hooks cannot live
 * behind the `semanticType` conditional that decides whether this section renders.
 */
function RouteTableSection({
	connectivity,
	routes,
	routeCount,
}: {
	connectivity: InsightConnectivityRef[];
	routes: InsightRouteEntry[];
	/** The table's real route total, which can exceed `routes.length` when Discovery didn't capture a destination as a plain CIDR. */
	routeCount?: unknown;
}) {
	const gateways = useCappedList(connectivity, SECTION_PREVIEW.gateways);
	const routeRows = useCappedList(routes, SECTION_PREVIEW.routes);
	const declaredRouteCount = typeof routeCount === "number" ? routeCount : null;

	if (connectivity.length === 0 && routes.length === 0) return null;

	return (
		<div className="flex flex-col gap-3" data-testid="detail-route-table-section">
			{connectivity.length > 0 && (
				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
						Gateways used ({connectivity.length})
					</span>
					<ul className="flex flex-wrap gap-1" data-testid="detail-gateway-list">
						{gateways.visible.map((target) => (
							<li key={target.id}>
								<span className="flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 py-0.5 pl-2 pr-1 font-mono text-[10px] text-emerald-800 dark:border-semantic-border-success-primary dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success">
									{target.nativeId ?? target.label}
									{typeof target.routeCount === "number" && (
										<span className="rounded-full bg-emerald-600 px-1 text-[9px] font-bold leading-[14px] text-white dark:bg-semantic-bg-success-bold">
											{target.routeCount}
										</span>
									)}
								</span>
							</li>
						))}
					</ul>
					<MoreToggle
						hidden={gateways.hidden}
						expanded={gateways.expanded}
						onToggle={gateways.toggle}
						testId="detail-gateway-list-toggle"
					/>
				</div>
			)}
			{routes.length > 0 && (
				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
						Routes ({declaredRouteCount !== null ? Math.max(declaredRouteCount, routes.length) : routes.length})
					</span>
					<ul
						className="flex flex-col overflow-hidden rounded-md border border-semantic-border-secondary bg-semantic-bg-primary"
						data-testid="detail-route-list"
					>
						{routeRows.visible.map((route, index) => (
							<li
								key={index}
								className="flex items-center justify-between gap-2 border-b border-semantic-border-secondary px-2 py-1 last:border-b-0"
							>
								<span className="shrink-0 font-mono text-[11px] font-medium text-semantic-text-primary">{route.destination}</span>
								<span className="text-[10px] text-semantic-text-tertiary">→</span>
								<span className="min-w-0 truncate font-mono text-[11px] text-semantic-text-secondary" title={route.targetLabel}>
									{route.targetLabel}
								</span>
							</li>
						))}
					</ul>
					<MoreToggle
						hidden={routeRows.hidden}
						expanded={routeRows.expanded}
						onToggle={routeRows.toggle}
						testId="detail-route-list-toggle"
					/>
					{/* The count includes gateway routes whose destination Discovery
					    didn't capture as a plain CIDR (IPv6/prefix-list) — said out
					    loud so the badge and this list never look contradictory. */}
					{declaredRouteCount !== null && declaredRouteCount > routes.length && (
						<span data-testid="detail-routes-uncaptured" className="text-[10px] text-semantic-text-tertiary">
							+{declaredRouteCount - routes.length} route{declaredRouteCount - routes.length === 1 ? "" : "s"} with uncaptured
							destination
						</span>
					)}
				</div>
			)}
		</div>
	);
}

export interface ResourceDetailPanelProps {
	topology: InsightTopologyResponse | null;
	selectedResourceId: string | null;
	onClose: () => void;
	/** Navigates to another resource (e.g. a sibling ENI, the owning instance, a Security Group chip) — optional so panels rendered without a selection setter still degrade to inert text. */
	onSelectResource?: (id: string) => void;
}

/** Every ENI in `topology.nodes` whose `resolvedInstance` points at `instanceId`, excluding `excludeId` — the same instance id referenced from multiple subnets resolves to one aggregated list here, never duplicate instance objects. */
function enisReferencingInstance(
	topology: InsightTopologyResponse,
	instanceId: string,
	excludeId?: string
): InsightTopologyResponse["nodes"] {
	return topology.nodes.filter((candidate) => {
		if (candidate.id === excludeId) return false;
		const data = (candidate.data ?? {}) as InsightNodeData;
		if (insightNodeTypeFor(candidate.type ?? data.kind, candidate.kind) !== "insightEni") return false;
		const resolvedInstance = data.resolvedInstance as InsightChipRef | null | undefined;
		return resolvedInstance?.id === instanceId;
	});
}

function subnetLabelFor(topology: InsightTopologyResponse, subnetId: string | undefined): string | null {
	if (!subnetId) return null;
	const subnet = topology.nodes.find((candidate) => candidate.id === subnetId);
	if (!subnet) return null;
	return String(((subnet.data ?? {}) as InsightNodeData).label ?? subnet.id);
}

function ReferenceButton({
	id,
	label,
	secondaryLabel,
	testId,
	onSelectResource,
	resolvable = true,
}: {
	id: string;
	label: string;
	/**
	 * Optional second line (e.g. the subnet an ENI lives in). Kept out of `label`
	 * on purpose: concatenated into one line, the variable-length half pushes the
	 * fixed-length identifier — the part you actually need to read — under the
	 * ellipsis. On its own line the identifier always fits and only this truncates.
	 */
	secondaryLabel?: string;
	testId: string;
	onSelectResource?: (id: string) => void;
	/** Whether `id` resolves to a node in this topology — a reference to something the digest doesn't carry as its own node (e.g. a NACL, which only exists as a decoration) renders as plain text instead of a click that would silently blank the panel. */
	resolvable?: boolean;
}) {
	// `block w-full min-w-0` is what makes `truncate` bite: a bare inline-block
	// button (or a flex child with an intrinsic min-width) sizes to its content and
	// overflows the panel instead of eliding, which is how long AWS names used to
	// spill past the panel's right edge.
	const shell = "block w-full min-w-0 text-left text-xs";
	const body = (
		<>
			<span className="block truncate">{label}</span>
			{secondaryLabel && (
				<span className="block truncate text-[11px] text-semantic-text-tertiary">{secondaryLabel}</span>
			)}
		</>
	);
	const fullTitle = secondaryLabel ? `${label} — ${secondaryLabel}` : label;

	if (!resolvable) {
		return (
			<span data-testid={testId} className={`${shell} text-semantic-text-primary`} title={fullTitle}>
				{body}
			</span>
		);
	}
	return (
		<button
			type="button"
			data-testid={testId}
			onClick={() => onSelectResource?.(id)}
			className={`${shell} text-semantic-text-brand hover:underline`}
			title={fullTitle}
		>
			{body}
		</button>
	);
}

/**
 * How many entries each long section of the panel shows before collapsing the
 * rest behind a toggle. Chosen per section by how tall one entry is: a route or a
 * reference is a full row, a gateway chip is a wrapped pill.
 *
 * These exist because real DISH data blows past any "it'll be a handful" guess —
 * a UPF route table on a vrouter fans out to sixteen ENI next hops and twenty
 * routes, which uncapped buries Tags and Attributes far below the fold.
 */
const SECTION_PREVIEW = {
	references: 5,
	gateways: 8,
	routes: 8,
} as const;

/**
 * Shows the first `cap` items and keeps the rest one click away.
 *
 * Preferred over capping a section's height with its own scrollbar: a nested
 * scroll area inside an already-scrolling panel is easy to miss and awkward to
 * drive with a trackpad.
 */
function useCappedList<T>(items: T[], cap: number) {
	const [expanded, setExpanded] = React.useState(false);
	return {
		visible: expanded ? items : items.slice(0, cap),
		hidden: Math.max(items.length - cap, 0),
		expanded,
		toggle: () => setExpanded((current) => !current),
	};
}

/** The "Show N more" / "Show fewer" control paired with `useCappedList`. Renders nothing when everything already fits. */
function MoreToggle({
	hidden,
	expanded,
	onToggle,
	testId,
}: {
	hidden: number;
	expanded: boolean;
	onToggle: () => void;
	testId: string;
}) {
	if (hidden === 0) return null;
	return (
		<button
			type="button"
			data-testid={testId}
			onClick={onToggle}
			className="w-fit text-[11px] font-medium text-semantic-text-brand hover:underline"
		>
			{expanded ? "Show fewer" : `Show ${hidden} more`}
		</button>
	);
}

/** A capped list of navigable resource references (sibling ENIs, an instance's interfaces). */
function ReferenceList({
	items,
	testId,
	onSelectResource,
}: {
	items: Array<{ id: string; label: string; secondaryLabel?: string; testId: string }>;
	testId: string;
	onSelectResource?: (id: string) => void;
}) {
	const { visible, hidden, expanded, toggle } = useCappedList(items, SECTION_PREVIEW.references);

	return (
		<>
			<ul className="flex flex-col gap-1" data-testid={testId}>
				{visible.map((item) => (
					<li key={item.id}>
						<ReferenceButton
							id={item.id}
							label={item.label}
							secondaryLabel={item.secondaryLabel}
							testId={item.testId}
							onSelectResource={onSelectResource}
						/>
					</li>
				))}
			</ul>
			<MoreToggle hidden={hidden} expanded={expanded} onToggle={toggle} testId={`${testId}-toggle`} />
		</>
	);
}

/**
 * `data` keys never shown as raw attribute rows: either rendered by a
 * dedicated section above (chips/references/routes), presentation-layer
 * state (`cardLayout`/`eniGrid`/scope-summary fields), or callbacks.
 */
const HIDDEN_DETAIL_KEYS = new Set([
	"label",
	"kind",
	"groupKind",
	"ctxrUuids",
	"onSelectRelated",
	"searchQuery",
	"securityGroups",
	"securityGroupsUnavailable",
	"resolvedInstance",
	"effectiveNacl",
	"effectiveRouteTable",
	"connectivity",
	"routes",
	"hiddenTotal",
	"hiddenAttached",
	"hiddenUnattached",
	"hiddenWarning",
	"hiddenError",
	"expanded",
	"onToggleExpand",
	"orphaned",
	"attributes",
	"eniGrid",
	"cardLayout",
	"scopeId",
	"scopeLevel",
	"summaryCounts",
	"pinned",
	"onTogglePin",
	"onFocus",
	"onExpandScope",
	"lowDetail",
]);

/** Canonical `attributes` keys whose values were already flattened/translated onto `data` under another name, or rendered by their own section — skipped so the same fact isn't listed twice. */
const FLATTENED_ATTRIBUTE_KEYS = new Set([
	"cidrBlock",
	"privateIpAddress",
	"publicIpAddress",
	"publicIp",
	"connectivityTargets",
	"routes",
	// Rendered by the dedicated Tags section below.
	"tags",
]);

/** The resource's own cloud tags, sorted by key so the list is stable between renders. Non-string values are stringified rather than dropped. */
function resourceTagEntries(data: InsightNodeData): Array<[string, string]> {
	const attributes = data.attributes;
	if (typeof attributes !== "object" || attributes === null || Array.isArray(attributes)) return [];
	const tags = (attributes as Record<string, unknown>).tags;
	if (typeof tags !== "object" || tags === null || Array.isArray(tags)) return [];
	return Object.entries(tags as Record<string, unknown>)
		.filter(([, value]) => value !== null && value !== undefined)
		.map(([key, value]) => [key, typeof value === "string" ? value : String(value)] as [string, string])
		.sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Every displayable detail row for the selected node: its own flattened
 * fields first, then each canonical `attributes` entry as its own row
 * (instead of the previous single raw-JSON blob) — "when we click a
 * resource, show as much of its data as possible".
 */
function detailRows(data: InsightNodeData): Array<[string, unknown]> {
	const rows: Array<[string, unknown]> = Object.entries(data).filter(
		([key, value]) => !HIDDEN_DETAIL_KEYS.has(key) && typeof value !== "function"
	);
	const shown = new Set(rows.map(([key]) => key));
	const attributes = data.attributes;
	if (typeof attributes === "object" && attributes !== null && !Array.isArray(attributes)) {
		Object.entries(attributes as Record<string, unknown>).forEach(([key, value]) => {
			if (FLATTENED_ATTRIBUTE_KEYS.has(key) || shown.has(key)) return;
			if (value === undefined || value === null || typeof value === "function") return;
			rows.push([key, value]);
		});
	}
	return rows;
}

/**
 * Shows the selected node's own attributes plus which contributing CTXR(s)
 * produced it (CTX-97: "show resource details, contributing CTXR
 * provenance, and freshness"). Freshness/status per contributing member
 * comes from `topology.provenance`, keyed by the node's own `ctxrUuids` —
 * a node touched by 2 cluster members shows both, so a member that's
 * `completed_with_warnings` is visible even if the other member is fully
 * `completed`.
 */
export const ResourceDetailPanel: React.FC<ResourceDetailPanelProps> = ({
	topology: rawTopology,
	selectedResourceId,
	onClose,
	onSelectResource,
}) => {
	// This panel reads `topology.nodes` straight off the response (it is not
	// downstream of the canvas's `toReactFlowNodes`), so a canonical nested
	// digest is flattened here too — a no-op (same references) for the
	// previous flattened format.
	const topology = useMemo(
		() => (rawTopology ? { ...rawTopology, nodes: normalizeCanonicalTopologyNodes(rawTopology.nodes) } : null),
		[rawTopology]
	);

	const node = useMemo(
		() => topology?.nodes.find((candidate) => candidate.id === selectedResourceId) ?? null,
		[topology, selectedResourceId]
	);

	const contributingMembers = useMemo(() => {
		if (!node || !topology) return [];
		const ctxrUuids = new Set(node.ctxrUuids ?? []);
		return topology.provenance.filter((member) => ctxrUuids.has(member.ctxrUuid));
	}, [node, topology]);

	const semanticType = useMemo(() => {
		if (!node) return null;
		const nodeData = (node.data ?? {}) as InsightNodeData;
		const rawKind = node.kind === "group" ? node.groupKind ?? nodeData.groupKind : node.type ?? nodeData.kind;
		return insightNodeTypeFor(rawKind, node.kind);
	}, [node]);

	/** CTX-128 — other ENIs sharing this ENI's resolved instance, so an instance spanning several subnets is discoverable from any one of its ENIs. */
	const siblingEnis = useMemo(() => {
		if (!topology || !node || semanticType !== "insightEni") return [];
		const resolvedInstance = (node.data as InsightNodeData | undefined)?.resolvedInstance as InsightChipRef | null | undefined;
		if (!resolvedInstance?.id) return [];
		return enisReferencingInstance(topology, resolvedInstance.id, node.id);
	}, [topology, node, semanticType]);

	/** CTX-128 — viewing the underlying instance (still present in `topology.nodes`, just never rendered as its own subnet card) aggregates every ENI that references it, across every subnet. */
	const instanceEnis = useMemo(() => {
		if (!topology || !node || semanticType !== "insightInstance") return [];
		return enisReferencingInstance(topology, node.id);
	}, [topology, node, semanticType]);

	if (!selectedResourceId) return null;

	// A click on one of the attachment nodes drawn above a collapsed VPC card
	// selects a synthetic id — resolved here into the family's real gateways,
	// each navigable to its own full detail (name, native id, attributes).
	const attachmentSelection = parseAttachmentSelection(selectedResourceId);
	if (attachmentSelection && topology) {
		const vpc = topology.nodes.find((candidate) => candidate.id === attachmentSelection.vpcId);
		const families = attachmentSelection.family === "all" ? ATTACHMENT_FAMILY_ORDER : [attachmentSelection.family];
		const sections = families
			.map((family) => ({ family, gateways: attachmentGatewaysFor(topology, attachmentSelection.vpcId, family) }))
			.filter((section) => section.gateways.length > 0);
		return (
			<div
				data-testid="resource-detail-panel"
				className="flex w-full flex-col gap-3 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt p-3 sm:w-80"
			>
				<div className="flex items-start justify-between gap-2">
					<div className="flex flex-col min-w-0">
						<span className="truncate text-sm font-semibold text-semantic-text-primary">
							{attachmentSelection.family === "all" ? "VPC attachments" : ATTACHMENT_FAMILY_TITLE[attachmentSelection.family]}
						</span>
						<span className="truncate text-xs text-semantic-text-tertiary">
							{String((vpc?.data as InsightNodeData | undefined)?.label ?? attachmentSelection.vpcId)}
						</span>
					</div>
					<button
						type="button"
						aria-label="Close resource details"
						onClick={onClose}
						className="shrink-0 rounded-sm p-1 text-semantic-text-secondary hover:bg-semantic-state-active"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
				<div className="flex flex-col gap-3" data-testid="detail-attachment-section">
					{sections.length === 0 ? (
						<span className="text-xs text-semantic-text-secondary">No gateways of this family in this VPC.</span>
					) : (
						sections.map((section) => (
							<div key={section.family} className="flex flex-col gap-1">
								{attachmentSelection.family === "all" && (
									<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
										{ATTACHMENT_FAMILY_TITLE[section.family]}
									</span>
								)}
								<ul className="flex flex-col overflow-hidden rounded-md border border-semantic-border-secondary bg-semantic-bg-primary">
									{section.gateways.map((gateway) => {
										const gatewayData = (gateway.data ?? {}) as InsightNodeData;
										const label = String(gatewayData.label ?? gateway.id);
										const nativeId = typeof gatewayData.nativeId === "string" ? gatewayData.nativeId : undefined;
										return (
											<li key={gateway.id} className="border-b border-semantic-border-secondary px-2 py-1.5 last:border-b-0">
												<ReferenceButton
													id={gateway.id}
													label={nativeId && nativeId !== label ? `${label} (${nativeId})` : label}
													testId={`detail-attachment-${gateway.id}`}
													onSelectResource={onSelectResource}
												/>
											</li>
										);
									})}
								</ul>
							</div>
						))
					)}
				</div>
			</div>
		);
	}

	if (!node) return null;

	const data = (node.data ?? {}) as InsightNodeData;
	const securityGroups = Array.isArray(data.securityGroups) ? (data.securityGroups as InsightChipRef[]) : [];
	const connectivity = Array.isArray(data.connectivity) ? (data.connectivity as InsightConnectivityRef[]) : [];
	const routes = Array.isArray(data.routes) ? (data.routes as InsightRouteEntry[]) : [];
	const nodeIds = new Set((topology as InsightTopologyResponse).nodes.map((candidate) => candidate.id));
	const effectiveNacl = (data.effectiveNacl ?? null) as InsightChipRef | null;
	const effectiveRouteTable = (data.effectiveRouteTable ?? null) as InsightChipRef | null;
	const tagEntries = resourceTagEntries(data);

	return (
		// A resource like a vrouter ENI carries far more than fits: security groups,
		// an instance, nine sibling ENIs, tags and ~15 attributes. The panel scrolls
		// itself (`min-h-0 overflow-y-auto`) instead of overflowing its parent, and
		// the identity header stays pinned so Close is reachable from anywhere in
		// that scroll.
		<div
			data-testid="resource-detail-panel"
			className="flex min-h-0 w-full shrink-0 flex-col gap-3 overflow-y-auto overscroll-contain rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt p-3 sm:w-80"
		>
			<div className="sticky -top-3 z-10 -mx-3 -mt-3 flex items-start justify-between gap-2 border-b border-semantic-border-secondary bg-semantic-bg-primary-alt px-3 pb-2 pt-3">
				<div className="flex min-w-0 flex-col gap-1">
					<span className="break-all text-sm font-semibold leading-snug text-semantic-text-primary">
						{data.label ?? node.id}
					</span>
					<span className="w-fit rounded-full bg-semantic-bg-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-semantic-text-secondary">
						{String(data.kind ?? node.type ?? "resource")}
					</span>
				</div>
				<button
					type="button"
					aria-label="Close resource details"
					onClick={onClose}
					className="shrink-0 rounded-sm p-1 text-semantic-text-secondary hover:bg-semantic-state-active"
				>
					<X className="h-4 w-4" />
				</button>
			</div>

			<div className="flex flex-col gap-1">
				<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
					Contributing CTXR provenance
				</span>
				{contributingMembers.length === 0 ? (
					<span className="text-xs text-semantic-text-secondary">No provenance recorded for this node.</span>
				) : (
					<ul className="flex flex-col gap-1">
						{contributingMembers.map((member) => (
							<li
								key={member.ctxrUuid}
								data-testid={`provenance-${member.ctxrUuid}`}
								className="flex items-center justify-between gap-2 text-xs"
							>
								<span className="truncate text-semantic-text-primary">{member.name}</span>
								<span
									className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
										member.status === "completed"
											? "bg-semantic-bg-success-secondary text-semantic-text-success"
											: "bg-semantic-bg-warning-secondary text-semantic-text-warning"
									}`}
								>
									{member.status}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>

			{semanticType === "insightEni" && (
				<div className="flex flex-col gap-1" data-testid="detail-eni-section">
					<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">Security Groups</span>
					{data.securityGroupsUnavailable ? (
						<span data-testid="detail-sg-unavailable" className="text-xs text-semantic-text-warning">
							Security Group data is unavailable for this resource — the processing role may be missing permission to
							enumerate Security Groups.
						</span>
					) : securityGroups.length > 0 ? (
						<ul className="flex flex-col gap-1">
							{securityGroups.map((sg) => (
								<li key={sg.id}>
									<ReferenceButton
										id={sg.id}
										label={sg.nativeId && sg.nativeId !== sg.label ? `${sg.label} (${sg.nativeId})` : sg.label}
										testId={`detail-sg-${sg.id}`}
										onSelectResource={onSelectResource}
										resolvable={nodeIds.has(sg.id)}
									/>
								</li>
							))}
						</ul>
					) : (
						<span className="text-xs text-semantic-text-secondary">No Security Groups.</span>
					)}

					{data.resolvedInstance ? (
						<>
							<span className="mt-2 text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">Instance</span>
							<ReferenceButton
								id={(data.resolvedInstance as InsightChipRef).id}
								label={(data.resolvedInstance as InsightChipRef).label}
								testId="detail-resolved-instance"
								onSelectResource={onSelectResource}
								resolvable={nodeIds.has((data.resolvedInstance as InsightChipRef).id)}
							/>
						</>
					) : (
						<span className="mt-2 text-xs font-medium uppercase tracking-wide text-semantic-text-tertiary">Standalone (no attached instance)</span>
					)}

					{siblingEnis.length > 0 && (
						<>
							<span className="mt-2 text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
								Sibling ENIs ({siblingEnis.length})
							</span>
							<ReferenceList
								testId="detail-sibling-eni-list"
								onSelectResource={onSelectResource}
								items={siblingEnis.map((sibling) => ({
									id: sibling.id,
									label: String((sibling.data as InsightNodeData | undefined)?.label ?? sibling.id),
									secondaryLabel: subnetLabelFor(topology as InsightTopologyResponse, sibling.parentId) ?? undefined,
									testId: `detail-sibling-eni-${sibling.id}`,
								}))}
							/>
						</>
					)}
				</div>
			)}

			{semanticType === "insightInstance" && instanceEnis.length > 0 && (
				<div className="flex flex-col gap-1" data-testid="detail-instance-section">
					<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
						Network Interfaces ({instanceEnis.length})
					</span>
					<ReferenceList
						testId="detail-instance-eni-list"
						onSelectResource={onSelectResource}
						items={instanceEnis.map((eni) => ({
							id: eni.id,
							label: String((eni.data as InsightNodeData | undefined)?.label ?? eni.id),
							secondaryLabel: subnetLabelFor(topology as InsightTopologyResponse, eni.parentId) ?? undefined,
							testId: `detail-instance-eni-${eni.id}`,
						}))}
					/>
				</div>
			)}

			{semanticType === "insightSubnet" && (effectiveRouteTable || effectiveNacl) && (
				<div className="flex flex-col gap-1" data-testid="detail-subnet-section">
					<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">Associations</span>
					{effectiveRouteTable && (
						<div className="flex items-center justify-between gap-2">
							<span className="shrink-0 text-xs text-semantic-text-tertiary">Route table</span>
							<ReferenceButton
								id={effectiveRouteTable.id}
								label={effectiveRouteTable.label}
								testId="detail-subnet-route-table"
								onSelectResource={onSelectResource}
								resolvable={nodeIds.has(effectiveRouteTable.id)}
							/>
						</div>
					)}
					{effectiveNacl && (
						<div className="flex items-center justify-between gap-2">
							<span className="shrink-0 text-xs text-semantic-text-tertiary">Network ACL</span>
							<ReferenceButton
								id={effectiveNacl.id}
								label={effectiveNacl.label}
								testId="detail-subnet-nacl"
								onSelectResource={onSelectResource}
								resolvable={nodeIds.has(effectiveNacl.id)}
							/>
						</div>
					)}
				</div>
			)}

			{semanticType === "insightRouteTable" && (
				<RouteTableSection connectivity={connectivity} routes={routes} routeCount={data.routeCount} />
			)}

			{/*
				Tags get their own key/value section rather than being one more
				`Attributes` row holding a JSON blob — for every resource type that
				carries them (route tables, subnets, ENIs, VPCs, security groups, NACLs,
				endpoints, transit gateways). insight-data-service used to keep only
				`tags.Name`, as the display name, and drop the rest; it now carries the
				whole map (processor 1.1.0).
			*/}
			{tagEntries.length > 0 && (
				<div className="flex flex-col gap-1">
					<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
						Tags ({tagEntries.length})
					</span>
					<dl
						data-testid="resource-tags"
						className="overflow-hidden rounded-md border border-semantic-border-secondary bg-semantic-bg-primary text-xs"
					>
						{tagEntries.map(([key, value]) => (
							<div
								key={key}
								className="flex items-baseline justify-between gap-3 border-b border-semantic-border-secondary px-2 py-1 last:border-b-0"
							>
								<dt className="shrink-0 font-medium text-semantic-text-secondary" title={key}>
									{key}
								</dt>
								<dd
									className="min-w-0 truncate text-right font-mono text-[11px] text-semantic-text-primary"
									title={value}
								>
									{value}
								</dd>
							</div>
						))}
					</dl>
				</div>
			)}

			<div className="flex flex-col gap-1">
				<span className="text-xs font-semibold uppercase tracking-wide text-semantic-text-tertiary">
					Attributes
				</span>
				<dl className="overflow-hidden rounded-md border border-semantic-border-secondary bg-semantic-bg-primary text-xs">
					{detailRows(data).map(([key, value]) => (
						<div key={key} className="flex items-baseline justify-between gap-3 border-b border-semantic-border-secondary px-2 py-1 last:border-b-0">
							<dt className="shrink-0 text-semantic-text-tertiary">{key}</dt>
							<dd
								className="min-w-0 truncate text-right font-mono text-[11px] text-semantic-text-primary"
								title={typeof value === "object" ? undefined : String(value)}
							>
								{typeof value === "object" && value !== null ? JSON.stringify(value) : String(value)}
							</dd>
						</div>
					))}
				</dl>
			</div>
		</div>
	);
};

export default ResourceDetailPanel;
